import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  Select,
  MenuItem,
  FormControl,
  TextField,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";

const BASE_URL = process.env.REACT_APP_BACKEND_BASE_URL;

const isDelegateActiveNow = (delegate) => {
  if (!delegate?.userId) return false;
  if (delegate.startDate && delegate.endDate) {
    const now = new Date();
    return now >= new Date(delegate.startDate) && now <= new Date(delegate.endDate);
  }
  return !!delegate.manualActive;
};

// Reusable dialog for setting an alternate approver ("מאשר חלופי") for
// `targetUser` — usable by the user herself, her direct manager, or an admin.
const DelegateDialog = ({ open, targetUser, users, execute, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);

  const [delegateUserId, setDelegateUserId] = useState("");
  const [manualActive, setManualActive] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const load = useCallback(async () => {
    if (!targetUser) return;
    setLoading(true);
    setError("");
    try {
      const res = await execute(
        "GET",
        `${BASE_URL}/api/tradeDelay/users/${targetUser._id}/delegate`,
      );
      const delegate = res?.delegate || {};
      setDelegateUserId(delegate.userId || "");
      setManualActive(!!delegate.manualActive);
      setStartDate(delegate.startDate ? delegate.startDate.slice(0, 10) : "");
      setEndDate(delegate.endDate ? delegate.endDate.slice(0, 10) : "");
    } catch {
      setError("שגיאה בטעינת הגדרות המאשר החלופי");
    } finally {
      setLoading(false);
    }
  }, [targetUser, execute]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  const hasDateRange = !!(startDate && endDate);
  const isActiveNow = isDelegateActiveNow({
    userId: delegateUserId,
    manualActive,
    startDate,
    endDate,
  });

  const handleSave = async () => {
    setSaving(true);
    setError("");
    setSaved(false);
    try {
      await execute(
        "PUT",
        `${BASE_URL}/api/tradeDelay/users/${targetUser._id}/delegate`,
        {
          delegateUserId: delegateUserId || null,
          manualActive,
          startDate: startDate || null,
          endDate: endDate || null,
        },
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(err?.data?.message || err?.msg || "שגיאה בשמירת המאשר החלופי");
    } finally {
      setSaving(false);
    }
  };

  const candidateUsers = (users || []).filter((u) => u._id !== targetUser?._id);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle
        sx={{ bgcolor: "#0054A5", color: "#fff", display: "flex", alignItems: "center", gap: 1 }}
      >
        <SwapHorizIcon />
        מאשר חלופי {targetUser ? `— ${targetUser.Name} ${targetUser.LastName}` : ""}
      </DialogTitle>

      <DialogContent sx={{ pt: 3 }}>
        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <Box display="flex" flexDirection="column" gap={2}>
            <Alert severity="info">
              המאשר החלופי יקבל בדיוק את אותה הגישה (אותם סניפים ואפשרות עריכה) כמו{" "}
              {targetUser?.Name}, כל עוד ההגדרה פעילה.
            </Alert>

            <Box>
              <Typography variant="body2" color="text.secondary" mb={0.5}>
                מאשר חלופי
              </Typography>
              <FormControl fullWidth>
                <Select
                  value={delegateUserId}
                  onChange={(e) => setDelegateUserId(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">ללא מאשר חלופי</MenuItem>
                  {candidateUsers.map((u) => (
                    <MenuItem key={u._id} value={u._id}>
                      {u.Name} {u.LastName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {delegateUserId && (
              <>
                <Box display="flex" gap={2}>
                  <TextField
                    label="מתאריך (אופציונלי)"
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                  <TextField
                    label="עד תאריך (אופציונלי)"
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    fullWidth
                  />
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={manualActive}
                      disabled={hasDateRange}
                      onChange={(e) => setManualActive(e.target.checked)}
                    />
                  }
                  label={
                    hasDateRange
                      ? "הפעלה/כיבוי ידני (מבוטל — קיים טווח תאריכים)"
                      : "פעיל כרגע (הפעלה/כיבוי ידני, ללא טווח תאריכים)"
                  }
                />

                <Box>
                  <Chip
                    label={isActiveNow ? "פעיל כעת" : "לא פעיל כעת"}
                    size="small"
                    sx={{
                      bgcolor: isActiveNow ? "#dcfce7" : "#f3f4f6",
                      color: isActiveNow ? "#15803d" : "#6b7280",
                      fontWeight: 600,
                    }}
                  />
                </Box>
              </>
            )}

            {error && <Alert severity="error">{error}</Alert>}
            {saved && <Alert severity="success">נשמר בהצלחה</Alert>}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} variant="outlined">
          סגור
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading || saving}
          sx={{ bgcolor: "#0054A5" }}
        >
          {saving ? "שומר..." : "שמור"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DelegateDialog;
